import { useEffect, useState } from "react";
import { api, ApiError } from "../api/client";
import { EmptyState, ErrorBox, SkeletonRows } from "../components/States";
import { fmtMoney } from "../format";
import { MeResponse } from "../types";
import { DealCard } from "./DealCard";
import { DealCreateForm } from "./DealCreateForm";

interface DealRow {
  id: number;
  instruction_no: number | null;
  deal_type: string | null;
  deal_format: string | null;
  status: string | null;
  amount: number | null;
  currency: string;
  base_currency: string;
  rate: number | null;
  fee_percent: number | null;
  deal_date: string | null;
  fixed_at: string | null;
  payment_confirmed_at: string | null;
  pending_approval: boolean;
  cancelled_at_status: string | null;
  operator_name: string | null;
  company: string | null;
  bank: string | null;
  counterparty: string | null;
  counterparty_id: number | null;
  operator_id: number | null;
  company_id: number | null;
  bank_id: number | null;
}

interface DealsListResp {
  deals: DealRow[];
  total: number;
  page: number;
  limit: number;
  has_next: boolean;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  new: { label: "Новая", cls: "bg-slate-200 text-slate-800" },
  fixed_prepay: { label: "Зафикс. (предоплата)", cls: "bg-blue-100 text-blue-800" },
  fixed_awaiting: { label: "Зафикс. (ожид.)", cls: "bg-blue-100 text-blue-800" },
  awaiting_funds: { label: "Ждёт рубли", cls: "bg-amber-100 text-amber-800" },
  postponed: { label: "Перенос", cls: "bg-red-100 text-red-700" },
  cancelled: { label: "Отменена", cls: "bg-red-100 text-red-700" },
  confirmed: { label: "Подтверждена", cls: "bg-green-100 text-green-800" },
  reopened: { label: "Возобновлена", cls: "bg-yellow-100 text-yellow-900" },
  returned: { label: "Возврат", cls: "bg-orange-100 text-orange-800" },
  export_fx_sent: { label: "Экспорт: FX отправлен", cls: "bg-indigo-100 text-indigo-800" },
  scheduled: { label: "Запланирована", cls: "bg-slate-100 text-slate-600" },
};

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return null;
  const cfg = STATUS_BADGE[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Все статусы" },
  { value: "NEW,FIXED_PREPAY,FIXED_AWAITING,AWAITING_FUNDS", label: "Активные" },
  { value: "CONFIRMED", label: "Подтверждённые" },
  { value: "CANCELLED", label: "Отменённые" },
  { value: "RETURNED", label: "Возвраты" },
  { value: "POSTPONED", label: "Переносы" },
];

export function DealsRegistryPage({
  me, trashed = false,
}: { me: MeResponse; trashed?: boolean }) {
  const [data, setData] = useState<DealsListResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDealId, setOpenDealId] = useState<number | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [dictionaries, setDictionaries] = useState<any>(null);
  const [dictionariesLoading, setDictionariesLoading] = useState(false);

  // Фильтры:
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [dealType, setDealType] = useState<string>("");
  const [statusSel, setStatusSel] = useState<string>("");
  const [counterpartyId, setCounterpartyId] = useState<string>("");
  const [operatorId, setOperatorId] = useState<string>("");
  const [companyId, setCompanyId] = useState<string>("");
  const [bankId, setBankId] = useState<string>("");
  const [q, setQ] = useState<string>("");
  const [page, setPage] = useState<number>(1);

  // Массовое выделение для bulk-операций (только админ).
  const [selectMode, setSelectMode] = useState<boolean>(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<boolean>(false);

  // Триггер перезагрузки после правки/отмены в карточке.
  const [refresh, setRefresh] = useState(0);

  const isAdmin = me.roles.includes("admin");
  const toggleSelected = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const loadDictionaries = async () => {
    if (dictionaries) return; // Already loaded
    setDictionariesLoading(true);
    try {
      const dict = await api.get<any>("/api/deals/_meta/dictionaries");
      setDictionaries(dict);
    } catch (e) {
      console.error("Failed to load dictionaries:", e);
    } finally {
      setDictionariesLoading(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const reason = prompt(
      `Удалить ${selected.size} заявок?\nКраткая причина (необязательно):`,
    );
    if (reason === null) return;
    setBulkBusy(true);
    try {
      const res = await api.post<{
        requested: number; deleted: number; errors: any[]; snapshot: string | null;
      }>("/api/deals/_bulk/delete", {
        deal_ids: Array.from(selected),
        reason: reason || "bulk",
        make_snapshot: true,
      });
      alert(
        `Удалено: ${res.deleted} / ${res.requested}.` +
        (res.snapshot ? `\nСнапшот: ${res.snapshot}` : "") +
        (res.errors.length ? `\nОшибок: ${res.errors.length}` : ""),
      );
      setSelected(new Set());
      setSelectMode(false);
      setRefresh((x) => x + 1);
    } catch (e) {
      alert(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);
    if (dealType) params.set("dealType", dealType);
    if (statusSel) params.set("status", statusSel);
    if (counterpartyId.trim()) params.set("counterpartyId", counterpartyId.trim());
    if (operatorId.trim()) params.set("operatorId", operatorId.trim());
    if (companyId.trim()) params.set("companyId", companyId.trim());
    if (bankId.trim()) params.set("bankId", bankId.trim());
    if (q.trim()) params.set("q", q.trim());
    if (trashed) params.set("trashed", "true");
    params.set("page", String(page));
    params.set("limit", "50");
    setLoading(true);
    setError(null);
    api
      .get<DealsListResp>(`/api/deals?${params.toString()}`)
      .then(setData)
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
  }, [
    dateFrom, dateTo, dealType, statusSel, counterpartyId, operatorId,
    companyId, bankId, q, page, refresh, trashed,
  ]);

  const resetFilters = () => {
    setDateFrom("");
    setDateTo("");
    setDealType("");
    setStatusSel("");
    setCounterpartyId("");
    setOperatorId("");
    setCompanyId("");
    setBankId("");
    setQ("");
    setPage(1);
  };

  if (openDealId !== null) {
    return (
      <DealCard
        dealId={openDealId}
        me={me}
        onClose={() => setOpenDealId(null)}
        onChanged={() => {
          // Обновляем список после правки/отмены/удаления.
          setRefresh((x) => x + 1);
        }}
        onDeleted={() => {
          setOpenDealId(null);
          setRefresh((x) => x + 1);
        }}
      />
    );
  }

  const typeEmoji = (t: string | null) =>
    t === "import" ? "📥" : t === "export" ? "📤" : "📄";

  return (
    <div className="space-y-3 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          {trashed ? "🗑 Корзина заявок" : "📒 Заявки"}
        </h1>
        <div className="flex items-center gap-3">
          {!trashed && (
            <button
              onClick={() => {
                loadDictionaries();
                setShowCreateForm(true);
              }}
              className="px-3 py-1.5 bg-tg-button-color text-white text-xs font-medium rounded hover:bg-blue-700"
              title="Создать новую заявку"
            >
              ➕ Новая заявка
            </button>
          )}
          {isAdmin && !trashed && (
            <button
              onClick={() => {
                setSelectMode((v) => !v);
                if (selectMode) setSelected(new Set());
              }}
              className="text-xs text-tg-link"
            >
              {selectMode ? "Выйти из выделения" : "Выделить"}
            </button>
          )}
          <button
            onClick={resetFilters}
            className="text-xs text-tg-link"
          >
            Сбросить фильтры
          </button>
        </div>
      </div>

      {/* Поиск + основной фильтр */}
      <div className="space-y-2">
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder={
            me.permissions.key_analytic_only
              ? "№ поручения, D-NNNNNN…"
              : "Поиск: № заявки, № поручения, D-NNNNNN…"
          }
          className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
        />

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-tg-hint">
            Дата от
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
          <label className="text-xs text-tg-hint">
            Дата до
            <input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-tg-hint">
            Тип
            <select
              value={dealType}
              onChange={(e) => { setDealType(e.target.value); setPage(1); }}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            >
              <option value="">Все</option>
              <option value="IMPORT">📥 Импорт</option>
              <option value="EXPORT">📤 Экспорт</option>
            </select>
          </label>
          <label className="text-xs text-tg-hint">
            Статус
            <select
              value={statusSel}
              onChange={(e) => { setStatusSel(e.target.value); setPage(1); }}
              className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
        </div>

        <details className="bg-tg-sectionBg rounded-lg px-3 py-2 text-xs text-tg-hint">
          <summary className="cursor-pointer select-none">Доп. фильтры</summary>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label>
              Контрагент (ID)
              <input
                value={counterpartyId}
                onChange={(e) => { setCounterpartyId(e.target.value); setPage(1); }}
                inputMode="numeric"
                className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
              />
            </label>
            <label>
              Оператор (ID)
              <input
                value={operatorId}
                onChange={(e) => { setOperatorId(e.target.value); setPage(1); }}
                inputMode="numeric"
                className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
              />
            </label>
            <label>
              Компания (ID)
              <input
                value={companyId}
                onChange={(e) => { setCompanyId(e.target.value); setPage(1); }}
                inputMode="numeric"
                className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
              />
            </label>
            <label>
              Банк (ID)
              <input
                value={bankId}
                onChange={(e) => { setBankId(e.target.value); setPage(1); }}
                inputMode="numeric"
                className="block w-full mt-0.5 px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
              />
            </label>
          </div>
        </details>
      </div>

      {error && <ErrorBox text={error} />}
      {loading && <SkeletonRows rows={5} />}

      {data && data.deals.length === 0 && !loading && (
        <EmptyState icon="📒" text="По текущим фильтрам заявок нет." />
      )}

      {data && data.deals.length > 0 && (
        <>
          <div className="text-xs text-tg-hint px-1">
            Найдено: {data.total.toLocaleString("ru-RU")} • стр. {data.page}
          </div>
          <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
            {data.deals.map((d) => (
              <button
                key={d.id}
                onClick={() => {
                  if (selectMode) {
                    toggleSelected(d.id);
                  } else {
                    setOpenDealId(d.id);
                  }
                }}
                className={
                  "w-full text-left px-3 py-2.5 flex items-start gap-3 active:bg-tg-secondaryBg/50 " +
                  (selectMode && selected.has(d.id) ? "bg-blue-50 dark:bg-blue-900/30" : "")
                }
              >
                {selectMode && (
                  <input
                    type="checkbox"
                    checked={selected.has(d.id)}
                    onChange={() => toggleSelected(d.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1 shrink-0"
                  />
                )}
                <div className="text-2xl leading-none mt-0.5">
                  {typeEmoji(d.deal_type)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-sm">
                      D-{String(d.id).padStart(6, "0")}
                      {d.instruction_no && (
                        <span className="ml-2 text-tg-hint text-xs font-normal">
                          № {d.instruction_no}
                        </span>
                      )}
                    </div>
                    <StatusBadge status={d.status} />
                    {d.pending_approval && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-900">
                        требует одобрения
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-tg-hint mt-0.5 truncate">
                    {d.counterparty || "—"} · {d.deal_date || "—"}
                  </div>
                  {(d.company || d.bank) && (
                    <div className="text-[11px] text-tg-hint truncate">
                      {d.company || "—"} • {d.bank || "—"}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold">
                    {d.amount !== null
                      ? `${fmtMoney(d.amount)} ${d.currency}`
                      : "—"}
                  </div>
                  {d.rate !== null && (
                    <div className="text-[11px] text-tg-hint">
                      курс {d.rate.toLocaleString("ru-RU")}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="flex justify-between items-center text-sm">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg bg-tg-secondaryBg disabled:opacity-40"
            >
              ← Назад
            </button>
            <div className="text-tg-hint text-xs">
              Стр. {data.page}
            </div>
            <button
              disabled={!data.has_next}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-lg bg-tg-secondaryBg disabled:opacity-40"
            >
              Вперёд →
            </button>
          </div>
        </>
      )}

      {/* Плавающая панель массовых действий. */}
      {selectMode && selected.size > 0 && (
        <div className="fixed bottom-20 left-0 right-0 z-20 px-3">
          <div className="max-w-3xl mx-auto bg-tg-bg border border-tg-secondaryBg rounded-xl shadow-xl p-3 flex items-center justify-between gap-3">
            <div className="text-sm">
              Выделено: <strong>{selected.size}</strong>
            </div>
            <button
              disabled={bulkBusy}
              onClick={bulkDelete}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50"
            >
              🗑 Удалить выбранные
            </button>
          </div>
        </div>
      )}

      {/* Модальное окно создания заявки */}
      {showCreateForm && dictionaries && (
        <DealCreateForm
          onClose={() => setShowCreateForm(false)}
          onSuccess={() => {
            setRefresh((x) => x + 1);
            setShowCreateForm(false);
          }}
          dictionaries={dictionaries}
        />
      )}
    </div>
  );
}
